// Rural Health landing — Fieldbook (leaf accent). Standalone, content preserved.
import { Link } from "react-router-dom";
import { PageMeta } from "@/components/PageMeta";
import { canonicalUrl } from "@/lib/canonical";
import FieldHeader from "@/components/field/FieldHeader";
import FieldFooter from "@/components/field/FieldFooter";
import "@/styles/design-tokens.css";
import "./rural-health.css";

function Stat({ value, label, source }: { value: string; label: string; source: string }) {
  return (
    <div className="stat">
      <div className="v">{value}</div>
      <div className="l">{label}</div>
      <div className="src">{source}</div>
    </div>
  );
}

function BundleCard({ icon, title, description, features }: { icon: string; title: string; description: string; features: string[] }) {
  return (
    <div className="card">
      <div className="ic"><i className={`fa-solid ${icon}`} /></div>
      <h3>{title}</h3>
      <p>{description}</p>
      <ul>{features.map((f) => <li key={f}><i className="fa-solid fa-check" />{f}</li>)}</ul>
    </div>
  );
}

function AudienceCard({ title, icon, description, items, cta }: { title: string; icon: string; description: string; items: { icon: string; text: string }[]; cta: { href: string; label: string } }) {
  return (
    <div className="card">
      <div className="ic"><i className={`fa-solid ${icon}`} /></div>
      <h3>{title}</h3>
      <p>{description}</p>
      <ul>{items.map((item) => <li key={item.text}><i className={`fa-solid ${item.icon}`} />{item.text}</li>)}</ul>
      <Link className="cardcta" to={cta.href}>{cta.label} →</Link>
    </div>
  );
}

export default function RuralHealthPage() {
  return (
    <div className="ffly ffly--public rural-page">
      <PageMeta
        title="Rural Health — Data Infrastructure for Rural Hospitals"
        description="88% of rural hospitals report interoperability gaps. FHIRfly provides the API layer RHTP-funded projects need to connect systems, validate claims, and resolve terminology."
        canonical={canonicalUrl("/rural-health")}
      />
      <FieldHeader />

      <div className="wrap">
        <section className="hero">
          <div>
            <span className="eyebrow"><i className="fa-solid fa-hospital" />Rural Health</span>
            <h1>Rural hospitals need <span className="leafword">data infrastructure</span>, not more portals</h1>
            <p className="lede">88% of rural hospitals report interoperability gaps that delay care and increase denials. FHIRfly provides the API layer RHTP-funded projects need to connect systems, validate claims, and resolve terminology — without building from scratch.</p>
            <div className="ctas">
              <Link className="btn-leaf" to="/dashboard">Get a free API key →</Link>
              <Link className="btn-ghost" to="/docs">Read the docs</Link>
            </div>
          </div>
          <div className="stats">
            <Stat value="88%" label="of rural hospitals report interoperability gaps" source="Black Book Research, 2026" />
            <Stat value="29%" label="higher claim denial rate vs. urban facilities" source="Black Book Research, 2026" />
            <Stat value="69%" label="of rural providers lack real-time eligibility verification" source="Black Book Research, 2026" />
          </div>
        </section>

        <section>
          <div className="sec-head">
            <h2>The Rural Health API bundle</h2>
            <p>Everything a rural health project needs to resolve codes, check connectivity, and validate claims — one API key, one SDK.</p>
          </div>
          <div className="grid3">
            <BundleCard icon="fa-book-medical" title="Terminology APIs" description="Resolve NDC, NPI, RxNorm, LOINC, ICD-10, CVX, and FDA labels. Full-text search across all systems, updated daily." features={["Code lookup & search", "Drug-to-drug interaction data", "Provider taxonomy & address"]} />
            <BundleCard icon="fa-network-wired" title="Connectivity Intelligence" description="Discover which payers, HIEs, and EHRs support FHIR, Direct, or X12 in any state. Know before you build." features={["Payer FHIR endpoint directory", "State HIE coverage maps", "EHR integration profiles"]} />
            <BundleCard icon="fa-file-invoice-dollar" title="Claims Validation" description="Pre-submission NCCI, MUE, and coverage checks that catch denials before they happen — built for rural revenue cycle." features={["NCCI PTP edit validation", "MUE unit limit checks", "ICD-10 ↔ CPT coverage rules"]} />
          </div>
        </section>

        <section>
          <div className="band">
            <div>
              <h2>How much are claim denials costing you?</h2>
              <p>Use our free calculator to estimate preventable denial losses and potential recovery.</p>
            </div>
            <Link to="/claims-calculator">Open calculator →</Link>
          </div>
        </section>

        <section>
          <div className="grid2">
            <AudienceCard title="For developers" icon="fa-code" description="Build rural health features into your app with a free API key, TypeScript SDK, and MCP server."
              items={[{ icon: "fa-key", text: "Free tier: 10,000 requests/month" }, { icon: "fa-cube", text: "TypeScript SDK with full type safety" }, { icon: "fa-robot", text: "MCP server for AI agent integration" }, { icon: "fa-rocket", text: "Quickstart: first call in 5 minutes" }]}
              cta={{ href: "/dashboard", label: "Get your API key" }} />
            <AudienceCard title="For integrators & SORHs" icon="fa-building" description="White-label FHIRfly into RHTP grant projects. OEM licensing, volume pricing, and dedicated support for state programs."
              items={[{ icon: "fa-handshake", text: "OEM & white-label licensing" }, { icon: "fa-chart-line", text: "Volume pricing for RHTP programs" }, { icon: "fa-headset", text: "Dedicated integration support" }, { icon: "fa-file-contract", text: "BAA & compliance documentation" }]}
              cta={{ href: "mailto:partnerships@fhirfly.io?subject=Rural Health Partnership", label: "Contact partnerships" }} />
          </div>
        </section>

        <section>
          <div className="blogcard">
            <div>
              <div className="k">From the blog</div>
              <h3>Rural Health Transformation Through Interoperability</h3>
              <p>How RHTP funding, FHIR APIs, and terminology services are closing the data gap for rural providers — and what it means for developers building in this space.</p>
            </div>
            <Link to="/blog/rural-health-transformation-interoperability">Read the post →</Link>
          </div>
        </section>

        <section>
          <div className="readiness">
            <div style={{ flex: 1 }}>
              <span className="badge"><i className="fa-solid fa-clipboard-check" />Free assessment</span>
              <h3>Rural Health Readiness Checker</h3>
              <p>A self-service tool that assesses your facility's interoperability readiness, identifies gaps in terminology coverage, and generates an actionable integration roadmap — tuned for RHTP grant requirements.</p>
              <ul>
                <li><i className="fa-solid fa-check" />Score your EHR, claims, and connectivity maturity</li>
                <li><i className="fa-solid fa-check" />Map gaps to the specific FHIRfly APIs that fill them</li>
                <li><i className="fa-solid fa-check" />Export a report for grant applications</li>
              </ul>
              <Link className="btn-leaf" to="/rural-health/readiness">Check your readiness →</Link>
            </div>
            <div className="viz"><i className="fa-solid fa-clipboard-check" /></div>
          </div>
        </section>

        <section className="res">
          <h3>External resources</h3>
          <div className="links">
            <a href="https://www.hrsa.gov/rural-health/grants/rural-hospitals" target="_blank" rel="noopener noreferrer">HRSA Rural Hospital Programs ↗</a>
            <a href="https://www.ruralhealth.us/" target="_blank" rel="noopener noreferrer">National Rural Health Association ↗</a>
            <a href="https://www.hrsa.gov/rural-health" target="_blank" rel="noopener noreferrer">HRSA Rural Health ↗</a>
            <a href="https://www.cms.gov/priorities/key-initiatives/burden-reduction/interoperability" target="_blank" rel="noopener noreferrer">CMS Interoperability Rules ↗</a>
            <a href="https://www.healthit.gov/topic/interoperability/policy/trusted-exchange-framework-and-common-agreement-tefca" target="_blank" rel="noopener noreferrer">TEFCA ↗</a>
          </div>
        </section>

        <section className="endcta">
          <h2>Ready to build for rural health?</h2>
          <p>Start with 10,000 free requests per month. No credit card required. Full access to terminology, connectivity, and claims validation APIs.</p>
          <div className="btns">
            <Link className="btn-leaf" to="/dashboard">Get started free →</Link>
            <Link className="btn-ghost" to="/pricing">View pricing</Link>
          </div>
        </section>
      </div>

      <FieldFooter variant="full" />
    </div>
  );
}
